import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { NotebookPen, Lock } from "lucide-react";
import api from "../lib/api";
import * as C from "../lib/crypto";
import { useAuth } from "../context/AuthContext";
import { useCrypto } from "../context/CryptoContext";

export const NotesCtx = createContext(null);

// Loads a student's private notes for one Beacon. The key is derived from their own identity key, so only they can read them.
export const useLessonNotes = (bid, enabled, opts = {}) => {
  const { user } = useAuth();
  const { getDmKey, status } = useCrypto();
  const [state, setState] = useState({ key: undefined, raw: {} });
  useEffect(() => {
    if (!enabled || !bid || !user?.id) return;
    let live = true;
    (async () => {
      const key = await getDmKey(user.id).catch(() => null);
      const { data } = await api.get(`/beacon/${bid}/notes`).catch(() => ({ data: [] }));
      if (live) setState({ key, raw: Object.fromEntries(data.map((n) => [n.item_id, n])) });
    })();
    return () => { live = false; };
  }, [bid, enabled, user?.id, status]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!enabled) return null;
  const read = async (iid) => { const n = state.raw[iid]; return n && state.key ? C.decryptText(state.key, n.ct, n.iv).catch(() => "") : ""; };
  const save = async (iid, text) => {
    if (!state.key) throw new Error("locked");
    const body = text.trim() ? await C.encryptText(state.key, text).then((e) => ({ ct: e.ctB64, iv: e.ivB64 })) : { ct: "", iv: "" };
    await api.put(`/beacon/${bid}/notes/${iid}`, body);
    setState((s) => ({ ...s, raw: { ...s.raw, [iid]: body.ct ? { item_id: iid, ...body } : undefined } }));
  };
  return { ready: state.key !== undefined, locked: state.key === null, has: (iid) => !!state.raw[iid], read, save, title: opts.title, blanks: opts.blanks };
};

export const LessonNotes = ({ it }) => {
  const notes = useContext(NotesCtx);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(null);
  const [status, setStatus] = useState("");
  const timer = useRef(null);
  useEffect(() => { if (open && text === null && notes?.ready) notes.read(it.id).then(setText); }, [open, notes?.ready]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!notes) return null;
  const change = (v) => {
    setText(v); setStatus("Saving...");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => notes.save(it.id, v).then(() => setStatus(v.trim() ? "Saved. Only you can read this." : "Note removed.")).catch(() => setStatus("Couldn't save. Is your vault unlocked?")), 900);
  };
  return (
    <div className="mt-2" data-testid={`lesson-notes-${it.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`lesson-notes-toggle-${it.id}`} className="inline-flex items-center gap-1.5 font-body text-xs text-zinc-400 hover:text-white">
        <NotebookPen size={13} /> {open ? "Hide my notes" : notes.has(it.id) ? "My notes" : "Add private notes"}
      </button>
      {open && (notes.locked
        ? <p className="font-body text-xs text-zinc-500 mt-1.5 flex items-center gap-1.5"><Lock size={11} /> Unlock your vault to write private notes.</p>
        : <div className="mt-1.5">
            <textarea value={text ?? ""} onChange={(e) => change(e.target.value)} rows={4} maxLength={20000} disabled={text === null} placeholder="Your private notes for this lesson. Encrypted so only you can read them, not even the teacher."
              data-testid={`lesson-notes-text-${it.id}`} className="w-full bg-[#0A0A0A] border border-white/10 px-2 py-1.5 font-body text-sm text-white outline-none focus:border-white/30" />
            <div className="font-body text-[11px] text-zinc-500 flex items-center gap-1" data-testid={`lesson-notes-status-${it.id}`}><Lock size={10} /> {status || "Private to you"}</div>
          </div>)}
    </div>
  );
};
